import React, { useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { GatsbyImage } from "gatsby-plugin-image";
import { Flex, Box, Divider, Text, Image, Link } from "theme-ui";
import { NavLink, StarRating, Tag } from "../atoms";
import icon from "../../../../shared-components/src/images/backIcon.png";
import iconFeatured from "../../../../shared-components/src/images/backIcon-feature.png";
import { BookOpen, Play } from "lucide-react";
import { colors, typography } from "../../styles/theme";

const StyledLink = styled(Link)`
  text-decoration: none;
  height: 100%;
  &:hover {
    text-decoration: none;
  }
`;

const TestimonialWrapper = styled.div`
  max-width: fit-content;
  margin: 0 auto;
  cursor: pointer;
  .testimonial-content {
    height: 100%;
  }
`;

const Testimonial = ({
  variant,
  subVariant,
  backgroundColor,
  image,
  imageUrl,
  reviewUrl,
  imageAltText,
  feedback,
  employeeName,
  employeeDesignation,
  rating,
  companyName,
  companyLogo,
  companyLogoUrl,
  companyLogoAltText,
  labelOne,
  labelOneColor,
  labelTwo,
  labelTwoColor,
  caseSection,
  caseSectionColor,
  caseSectionLink,
  videoURL,
  customStyles,
  wrapper1CustomeStyles,
  wrapper2CustomeStyles,
  innerContainerCustomStyles,
}) => {
  let renderedComponent = null;
  const [expanded, setExpanded] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  if (variant === "default") {
    renderedComponent = (
      <Box sx={{ ...testimonialContainer, ...customStyles }}>
        <Flex sx={imageContainer}>
          {image && (
            <Box sx={employeeImage}>
              <GatsbyImage
                image={image}
                alt={imageAltText}
                style={{ width: "100%", height: "100%" }}
              />
            </Box>
          )}
          {imageUrl && (
            <Box sx={employeeImage}>
              <Image
                src={imageUrl}
                alt={imageAltText}
                sx={{ width: "100%", height: "100%" }}
              />
            </Box>
          )}
          {companyLogoUrl && (
            <Box sx={companyLogoDefaultBox}>
              <Image
                src={companyLogoUrl}
                alt={companyLogoAltText}
                sx={{ width: "100%", height: "100%" }}
              />
            </Box>
          )}
          {companyLogo && (
            <Box sx={companyLogoDefaultBox}>
              <GatsbyImage
                image={companyLogo}
                alt={companyLogoAltText}
                imgStyle={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}
        </Flex>
        <Text
          sx={feedbackText}
          dangerouslySetInnerHTML={{ __html: feedback }}
        />
        <Divider sx={divider} />
        <Flex sx={infoContainer}>
          <Flex sx={employeeDetails}>
            <Text sx={employeeNameStyles}>{employeeName}</Text>
            <Text sx={employeeDesignationStyles}>{employeeDesignation}</Text>
            {companyName && (
              <Text sx={empCompanyNameStyles}>{companyName}</Text>
            )}
          </Flex>
          {rating && <StarRating rating={rating} />}
        </Flex>
      </Box>
    );
  } else if (variant === "primary") {
    renderedComponent = (
      <Box sx={{ ...testimonialBox, ...customStyles }}>
        <Flex sx={flexColumn}>
          <Box>
            <Flex sx={infoContainer}>
              {rating && <StarRating rating={rating} />}
              {companyLogo && (
                <Box sx={companyLogoBox}>
                  <GatsbyImage
                    image={companyLogo}
                    alt={companyLogoAltText}
                    imgStyle={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}
              {companyLogoUrl && (
                <Box sx={companyLogoBox}>
                  <Image
                    src={companyLogoUrl}
                    alt={companyLogoAltText}
                    sx={{ width: "100%", height: "100%" }}
                  />
                </Box>
              )}
            </Flex>
            <Divider sx={dividerV2} />
            <Text
              sx={feedbackTextV2}
              dangerouslySetInnerHTML={{ __html: feedback }}
            />
          </Box>

          <Flex sx={employeeProfile}>
            {image && (
              <Box sx={employeeImageV2}>
                <GatsbyImage
                  image={image}
                  alt={imageAltText}
                  style={{ width: "100%", height: "100%" }}
                />
              </Box>
            )}
            {imageUrl && (
              <Box sx={employeeImageV2}>
                <Image
                  src={imageUrl}
                  alt={imageAltText}
                  sx={{ width: "100%", height: "100%" }}
                />
              </Box>
            )}
            <Flex sx={employeeDetails}>
              <Text sx={employeeNameStyles}>{employeeName}</Text>
              <Text sx={employeeDesignationStyles}>{employeeDesignation}</Text>
              {companyName && (
                <Text sx={empCompanyNameStyles}>{companyName}</Text>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Box>
    );
  } else if (variant === "secondary") {
    if (subVariant === "secondaryOne") {
      renderedComponent = (
        <Box
          sx={{
            ...testimonialBox,
            p: "1rem",
            backgroundColor: colors.primary100,
            width: ["15.625rem", "15.625rem", "17.5rem", "25.813rem"],
            ...customStyles,
          }}
        >
          <Flex sx={{ ...flexColumn, gap: "3rem", ...innerContainerCustomStyles }}>
            <Box>
              <Flex
                sx={{
                  ...infoContainer,
                  maxWidth: ["13.625rem", "13.625rem", "15.5rem", "23.813rem"],
                }}
              >
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  <Image
                    src={icon}
                    alt="quote"
                    sx={{
                      width: ["2rem", "2rem", "3rem", "3.5rem"],
                      height: ["1.5rem", "1.5rem", "2.5rem", "2.5rem"],
                    }}
                  />
                </Box>
                {rating && (
                  <StarRating
                    rating={rating}
                    customStyles={{
                      width: ["8.25rem", "8.25rem", "8.25rem", "9.5rem"],
                      height: ["1.25rem", "1.25rem", "1.25rem", "1.5rem"],
                    }}
                  />
                )}
              </Flex>
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Text
                  sx={{
                    ...feedbackTextV3,
                    fontSize: [
                      typography.textMd500.fontSize,
                      typography.textMd500.fontSize,
                      typography.textMd500.fontSize,
                      typography.textLg500.fontSize,
                    ],
                    lineHeight: [
                      typography.textMd500.lineHeight,
                      typography.textMd500.lineHeight,
                      typography.textMd500.lineHeight,
                      typography.textLg500.lineHeight,
                    ],
                    p: { m: 0 },
                    mt: "1.25rem",
                  }}
                  dangerouslySetInnerHTML={{ __html: feedback }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                ...infoContainer,
                maxWidth: ["13.625rem", "13.625rem", "15.5rem", "23.813rem"],
              }}
            >
              <Flex sx={{ height: "100%" }}>
                {image && (
                  <Box sx={employeeImageV2}>
                    <GatsbyImage
                      image={image}
                      alt={imageAltText}
                      style={{ width: "100%", height: "100%" }}
                    />
                  </Box>
                )}
                {imageUrl && (
                  <Box sx={employeeImageV2}>
                    <Image
                      src={imageUrl}
                      alt={imageAltText}
                      sx={{ width: "100%", height: "100%" }}
                    />
                  </Box>
                )}
                <Flex
                  sx={{
                    ...employeeDetails,
                    maxWidth: ["7.25rem", "7.25rem", "9rem", "12.313rem"],
                  }}
                >
                  <Text sx={employeeNameStyles}>{employeeName}</Text>
                  <Text sx={employeeDesignationStyles}>
                    {employeeDesignation}
                  </Text>
                  <Text sx={empCompanyNameStyles}>{companyName}</Text>
                </Flex>
              </Flex>
              {companyLogo && (
                <Box
                  sx={{
                    ...companyLogoBox,
                    mt: "1rem",
                    width: "2.5rem",
                    height: "2.5rem",
                  }}
                >
                  <GatsbyImage
                    image={companyLogo}
                    alt={companyLogoAltText}
                    style={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </Box>
              )}
              {companyLogoUrl && (
                <Box sx={companyLogoBox}>
                  <Image
                    src={companyLogoUrl}
                    alt={companyLogoAltText}
                    sx={{ width: "100%", height: "100%" }}
                  />
                </Box>
              )}
            </Box>
          </Flex>
        </Box>
      );
    } else if (subVariant === "secondaryTwo") {
      renderedComponent = (
        <Box
          sx={{
            ...testimonialBox,
            width: ["11.25rem", "11.25rem", "11.25rem", "14.875rem"],
            ...customStyles,
          }}
        >
          <Flex sx={flexColumn}>
            <Box>
              <Flex sx={infoContainer}>
                {rating && <StarRating rating={rating} />}
                {companyLogo && (
                  <Box sx={companyLogoBox}>
                    <GatsbyImage
                      image={companyLogo}
                      alt={companyLogoAltText}
                      imgStyle={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                )}
                {companyLogoUrl && (
                  <Box sx={companyLogoBox}>
                    <Image
                      src={companyLogoUrl}
                      alt={companyLogoAltText}
                      sx={{ width: "100%", height: "100%" }}
                    />
                  </Box>
                )}
              </Flex>
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Image
                  src={icon}
                  alt="quote"
                  sx={{
                    width: "4.75rem",
                    height: "12rem",
                    position: "absolute",
                    top: "-5rem",
                    left: "-1rem",
                    zIndex: -1,
                  }}
                />
                <Text
                  sx={{ ...feedbackTextV3 }}
                  dangerouslySetInnerHTML={{ __html: feedback }}
                />
              </Box>
            </Box>
            <Flex sx={employeeProfile}>
              {image && (
                <Box sx={employeeImageV2}>
                  <GatsbyImage
                    image={image}
                    alt={imageAltText}
                    style={{ width: "100%", height: "100%" }}
                  />
                </Box>
              )}
              {imageUrl && (
                <Box sx={employeeImageV2}>
                  <Image
                    src={imageUrl}
                    alt={imageAltText}
                    sx={{ width: "100%", height: "100%" }}
                  />
                </Box>
              )}
              <Flex sx={employeeDetails}>
                <Text sx={employeeNameStyles}>{employeeName}</Text>
                <Text sx={employeeDesignationStyles}>
                  {employeeDesignation}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Box>
      );
    }
  } else if (variant === "banner") {
    renderedComponent = (
      <Box
        sx={{
          backgroundColor: backgroundColor,
          ...bannerContainer,
          ...customStyles,
        }}
      >
        <Box>
          {rating && (
            <StarRating
              rating={rating}
              customStyles={{
                justifyContent: "center",
              }}
            />
          )}
          <Box
            sx={{
              mt: ["1rem", "1rem", "1rem", "1.5rem"],
              textAlign: "center",
            }}
          >
            <Text sx={{ ...typography.h3, color: colors.primaryMain }}>"</Text>
            <Text
              sx={bannerFeedback}
              dangerouslySetInnerHTML={{ __html: feedback }}
            />
            <Text sx={{ ...typography.h3, color: colors.primaryMain }}>"</Text>
          </Box>
          <Flex
            sx={{
              mt: ["1rem", "1rem", "1rem", "1.5rem"],
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {image && (
              <Box sx={employeeImageV3}>
                <GatsbyImage
                  image={image}
                  alt={imageAltText}
                  style={{ width: "100%", height: "100%" }}
                />
              </Box>
            )}
            {imageUrl && (
              <Box sx={employeeImageV3}>
                <Image
                  src={imageUrl}
                  alt={imageAltText}
                  sx={{ width: "100%", height: "100%" }}
                />
              </Box>
            )}
            <Flex sx={employeeDetails}>
              <Text sx={bannerEmployeeName}>{employeeName}</Text>
              <Text sx={bannerEmployeeDesignation}>{employeeDesignation}</Text>
              {companyName && (
                <Text sx={bannerEmployeeDesignation}>{companyName}</Text>
              )}
            </Flex>
          </Flex>
        </Box>
      </Box>
    );
  } else if (variant === "featured") {
    renderedComponent = (
      <Flex sx={{ ...featuredContainerStyles, ...customStyles }}>
        {(image || imageUrl) && (
          <Box sx={featuredImageStyles}>
            {image && (
              <GatsbyImage
                image={image}
                alt={imageAltText}
                style={{ width: "100%", height: "100%" }}
              />
            )}
            {imageUrl && (
              <Image
                src={imageUrl}
                alt={imageAltText}
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
          </Box>
        )}
        <Box sx={{ flex: 1, position: "relative", zIndex: 1 }}>
          <Text
            sx={{ ...typography.textLg500, mb: 3 }}
            dangerouslySetInnerHTML={{
              __html: `${feedback}`,
            }}
          />
          {(labelOne || labelTwo) && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", my: "1.5rem" }}>
              <Tag label={labelOne} bgColor={colors.secondaryGreen200} />
              <Tag label={labelTwo} bgColor={colors.secondaryBlue200} />
            </Box>
          )}
          <Box
            sx={{
              width: "100%",
              height: "1px",
              backgroundColor: colors.neutral200,
            }}
          />
          <Flex sx={{ justifyContent: "space-between", alignItems: "flex-end" }}>
            <Flex sx={{ ...employeeDetails, flexDirection: "column", mt: "1.5rem" }}>
              <Text sx={{ ...typography.h5, mb: "0.25rem" }}>{employeeName}</Text>
              {employeeDesignation && (<Text sx={{ ...typography.h7, mb: "1rem" }}> {employeeDesignation} </Text>)}
              {companyLogoUrl && (
                <Box sx={{ width: ["7.25rem", "7.25rem", "9.688rem"] }}>
                  <Image
                    src={companyLogoUrl}
                    alt={companyLogoAltText}
                    sx={{ width: "100%", height: "auto", objectFit: "contain" }}
                  />
                </Box>
              )}
            </Flex>
            {caseSection && caseSectionLink && (
              <NavLink
                to={caseSectionLink}
                customStyles={{
                  textDecoration: "none",
                  "&:hover span": {
                    textDecoration: "underline",
                  },
                }}
              >
                <Flex sx={featuredCaseSectionStyles}>
                  <BookOpen size={24} color={colors.primaryMain} />
                  <Text sx={{ ...typography.textMd500, color: colors.primaryMain }}>
                    {caseSection}
                  </Text>
                </Flex>
              </NavLink>
            )}
          </Flex>
        </Box>
        <Box sx={featuredIconStyles}>
          <Image
            src={iconFeatured}
            alt="quote"
            sx={{ width: "4.875rem", height: "3.5rem" }}
          />
        </Box>
      </Flex>
    );
  } else if (variant === "main") {
    const mainExpandedModal =
      expanded &&
      typeof document !== "undefined" &&
      createPortal(
        <Flex
          sx={videoModalStyles}
          onClick={() => setExpanded(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Full testimonial"
        >
          <Box
            sx={{
              maxWidth: "90%",
              width: ["20rem", "24.75rem", "33.5rem"],
              maxHeight: "90vh",
              overflow: "auto",
              backgroundColor: colors.white,
              borderRadius: "1.25rem",
              position: "relative",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ p: "1.5rem" }}>
              <Box
                onClick={() => setExpanded(false)}
                sx={{
                  cursor: "pointer",
                  width: "fit-content",
                  mb: !(labelOne || labelTwo) ? "0.5rem" : 0,
                }}
              >
                <Text
                  sx={{ ...mainTextBoxStyles, WebkitLineClamp: "unset", mb: 2 }}
                  dangerouslySetInnerHTML={{ __html: feedback ?? "" }}
                />
                <Text
                  sx={{
                    ...typography.textMd500,
                    cursor: "pointer",
                    width: "fit-content",
                    mt: 2,
                    borderColor: colors[`${caseSectionColor}Main`],
                    textDecoration: "underline",
                  }}
                >
                  Read Less
                </Text>
              </Box>
              {(labelOne || labelTwo) && (
                <Flex sx={{ gap: 2, flexWrap: "wrap", my: "1.5rem" }}>
                  {labelOne && (
                    <Tag
                      label={labelOne}
                      bgColor={colors[`${labelOneColor}200`]}
                    />
                  )}
                  {labelTwo && (
                    <Tag
                      label={labelTwo}
                      bgColor={colors[`${labelTwoColor}200`]}
                    />
                  )}
                </Flex>
              )}
            </Box>
            <Box
              sx={{
                width: "100%",
                height: "1px",
                backgroundColor: colors.neutral200,
              }}
            />
            <Flex
              sx={{
                ...mainImageBoxStyles,
                backgroundColor: colors[`${caseSectionColor}100`],
              }}
            >
              <Flex sx={{ flexDirection: "column", gap: !(companyLogoUrl || companyLogo) ? 0 : "0.25rem", width: "100%" }}>
                <Flex sx={{ alignItems: "flex-start", gap: 3 }}>
                  {(image || imageUrl) && (
                    <Box
                      sx={{
                        width: "3.438rem",
                        height: "3.438rem",
                        flexShrink: 0,
                        borderRadius: "1rem",
                        overflow: "hidden",
                      }}
                    >
                      {image && (
                        <GatsbyImage
                          image={image}
                          alt={imageAltText}
                          style={{ width: "100%", height: "100%" }}
                        />
                      )}
                      {imageUrl && (
                        <Image
                          src={imageUrl}
                          alt={imageAltText}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      )}
                    </Box>
                  )}
                  <Flex sx={{ flexDirection: "column" }}>
                    <Text
                      sx={{
                        ...typography.h5,
                        mb: "0.25rem",
                        color: colors.black,
                      }}
                    >
                      {employeeName}
                    </Text>
                    {employeeDesignation && (
                      <Text sx={{ ...typography.h7, color: colors.neutral900 }}>
                        {employeeDesignation}
                      </Text>
                    )}
                  </Flex>
                </Flex>
                <Flex
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    mt: "1.75rem",
                  }}
                >
                  {(companyLogoUrl || companyLogo) && (
                    <Box>
                      {companyLogoUrl && (
                        <Image
                          src={companyLogoUrl}
                          alt={companyLogoAltText}
                          sx={{
                            width: "fit-content",
                            height: "100%",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      )}
                      {companyLogo && !companyLogoUrl && ( 
                        <GatsbyImage
                          image={companyLogo}
                          alt={companyLogoAltText}
                          style={{ width: "fit-content", height: "100%" }}
                          imgStyle={{ objectFit: "contain" }}
                        />
                      )}
                    </Box>
                  )}
                  {caseSection && caseSectionLink && (
                    <NavLink
                      to={caseSectionLink}
                      customStyles={{
                        textDecoration: "none",
                        "&:hover span": { textDecoration: "underline" },
                      }}
                    >
                      <Flex sx={featuredCaseSectionStyles}>
                        <BookOpen size={24} color={colors.primaryMain} />
                        <Text
                          sx={{
                            ...typography.textMd500,
                            color: colors.primaryMain,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {caseSection}
                        </Text>
                      </Flex>
                    </NavLink>
                  )}
                </Flex>
              </Flex>
            </Flex>
          </Box>
        </Flex>,
        document.body,
      );

    renderedComponent = (
      <>
        <Flex
          sx={{
            ...mainContainerStyles,
            ...customStyles,
            "&:hover": {
              borderColor: colors[`${caseSectionColor}Main`],
            },
          }}
        >
          <Box sx={{ flex: 1, position: "relative", zIndex: 1 }}>
            <Box sx={{ px: "1.5rem", pt: "1.75rem" }}>
              <Box
                onClick={() => setExpanded(true)}
                sx={{
                  cursor: "pointer",
                  width: "fit-content",
                  mb: !(labelOne || labelTwo) ? "1.5rem" : 0,
                }}
              >
                <Text
                  sx={{ ...mainTextBoxStyles, WebkitLineClamp: 4 }}
                  dangerouslySetInnerHTML={{ __html: feedback ?? "" }}
                />
                <Text
                  sx={{
                    ...typography.textMd500,
                    borderColor: colors[`${caseSectionColor}Main`],
                    textDecoration: "underline",
                  }}
                >
                  Read More
                </Text>
              </Box>
              {(labelOne || labelTwo) && (
                <Flex sx={{ gap: 2, flexWrap: "wrap", my: "1.5rem" }}>
                  {labelOne && (
                    <Tag
                      label={labelOne}
                      bgColor={colors[`${labelOneColor}200`]}
                    />
                  )}
                  {labelTwo && (
                    <Tag
                      label={labelTwo}
                      bgColor={colors[`${labelTwoColor}200`]}
                    />
                  )}
                </Flex>
              )}
            </Box>
            <Box
              sx={{
                width: "100%",
                height: "1px",
                backgroundColor: colors.neutral200,
              }}
            />
            <Flex
              sx={{
                ...mainImageBoxStyles,
                backgroundColor: colors[`${caseSectionColor}100`],
              }}
            >
              <Flex sx={{ flexDirection: "column", gap: !(companyLogoUrl || companyLogo) ? 0 : "0.25rem", width: "100%" }}>
                <Flex sx={{ alignItems: "flex-start", gap: 3 }}>
                  {(image || imageUrl) && (
                    <Box
                      sx={{
                        width: "3.438rem",
                        height: "3.438rem",
                        flexShrink: 0,
                        borderRadius: "1rem",
                        overflow: "hidden",
                      }}
                    >
                      {image && (
                        <GatsbyImage
                          image={image}
                          alt={imageAltText}
                          style={{ width: "100%", height: "100%" }}
                        />
                      )}
                      {imageUrl && (
                        <Image
                          src={imageUrl}
                          alt={imageAltText}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      )}
                    </Box>
                  )}
                  <Flex sx={{ flexDirection: "column" }}>
                    <Text
                      sx={{
                        ...typography.h5,
                        mb: "0.25rem",
                        color: colors.black,
                      }}
                    >
                      {employeeName}
                    </Text>
                    {employeeDesignation && (
                      <Text sx={{ ...typography.h7, color: colors.neutral900 }}>
                        {employeeDesignation}
                      </Text>
                    )}
                  </Flex>
                </Flex>
                <Flex
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    mt: "1.75rem",
                  }}
                >
                  {(companyLogoUrl || companyLogo) && (
                    <Box>
                      {companyLogoUrl && (
                        <Image
                          src={companyLogoUrl}
                          alt={companyLogoAltText}
                          sx={{
                            width: "fit-content",
                            height: "100%",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      )}
                      {companyLogo && !companyLogoUrl && ( 
                        <GatsbyImage
                          image={companyLogo}
                          alt={companyLogoAltText}
                          style={{ width: "fit-content", height: "100%" }}
                          imgStyle={{ objectFit: "contain" }}
                        />
                      )}
                    </Box>
                  )}
                  {caseSection && caseSectionLink && <BookOpen size={24} />}
                </Flex>
              </Flex>
            </Flex>
          </Box>
        </Flex>
        {mainExpandedModal}
      </>
    );
  } else if (variant === "video") {
    renderedComponent = (
      <Flex sx={{ ...videoContainerStyles, ...customStyles }}>
        <Box
          sx={videoImageStyles}
          onClick={() => setIsVideoModalOpen(true)}
        >
          {image && (
            <GatsbyImage
              image={image}
              alt={imageAltText}
              style={{ width: "100%", height: "100%" }}
            />
          )}
          {imageUrl && (
            <Image
              src={imageUrl}
              alt={imageAltText}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          )}
          <Box sx={{ position: "absolute", inset: 0 }} />
          <Flex sx={videoPlayContainerStyles}>
            <Play size={62} color={colors.white}/>
          </Flex>
          <Flex sx={videoInnerStyles} >
            <Text sx={{ ...typography.h5, mb: "0.25rem", color: colors.white }}>
              {employeeName}
            </Text>
            {employeeDesignation && (
              <Text sx={{ ...typography.h7, color: colors.white }}>
                {employeeDesignation}
              </Text>
            )}
            {companyName && (
              <Text sx={{ ...typography.h7, color: colors.white }}>
                {companyName}
              </Text>
            )}
          </Flex>
        </Box>
        {isVideoModalOpen &&
          typeof document !== "undefined" &&
          createPortal(
            <Flex
              sx={videoModalStyles}
              onClick={() => setIsVideoModalOpen(false)}
            >
              <Box
                sx={{
                  width: ["90%", "80%", "60%"],
                  height: ["15.625rem", "21.875rem", "28.125rem"],
                  position: "relative",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <iframe
                  src={`${videoURL}?autoplay=1&loop=1&rel=0`}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    borderRadius: "0.75rem",
                    border: "none",
                  }}
                />
                <Box
                  onClick={() => setIsVideoModalOpen(false)}
                  sx={videoModalCloseStyles}
                >
                  ✕
                </Box>
              </Box>
            </Flex>,
            document.body,
          )}
        <Box sx={{ backgroundColor: colors.primary100, py: "1.75rem", px: "2rem" }}>
          <Text
            sx={{ ...typography.textMd500 }}
            dangerouslySetInnerHTML={{
              __html: `${feedback}`,
            }}
          />
        </Box>
      </Flex>
    );
  }

  const handleLinkClick = (e) => {
    if (!e.target.closest(".testimonial-content")) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  if (reviewUrl) {
    return (
      <TestimonialWrapper
        onClick={handleLinkClick}
        style={wrapper1CustomeStyles}
      >
        <StyledLink href={reviewUrl} target="_blank" rel="noopener noreferrer">
          <div style={wrapper2CustomeStyles} className="testimonial-content">
            {renderedComponent}
          </div>
        </StyledLink>
      </TestimonialWrapper>
    );
  } else {
    return renderedComponent;
  }
};

const {
  testimonialContainer,
  imageContainer,
  employeeImage,
  employeeImageV2,
  feedbackText,
  feedbackTextV2,
  feedbackTextV3,
  divider,
  dividerV2,
  employeeDetails,
  employeeNameStyles,
  employeeDesignationStyles,
  empCompanyNameStyles,
  infoContainer,
  testimonialBox,
  flexColumn,
  companyLogoBox,
  employeeProfile,
  companyLogoDefaultBox,
  employeeImageV3,
  bannerContainer,
  bannerFeedback,
  bannerEmployeeName,
  bannerEmployeeDesignation,
  featuredContainerStyles,
  featuredImageStyles,
  featuredIconStyles,
  featuredCaseSectionStyles,
  mainContainerStyles,
  mainTextBoxStyles,
  mainImageBoxStyles,
  videoContainerStyles,
  videoPlayContainerStyles,
  videoInnerStyles,
  videoImageStyles,
  videoModalStyles,
  videoModalCloseStyles,
} = {
  testimonialContainer: {
    position: "relative",
    backgroundColor: colors.white,
    border: `1px solid ${colors.neutral200}`,
    borderRadius: "20px",
    padding: [
      "4.25rem 2.313rem 2.37rem",
      "4.25rem 2.313rem 2.37rem",
      "4.188rem 4.375rem 3.563rem",
      "4.188rem 4.375rem 3.563rem",
    ],
    position: "relative",
    width: "100%",
    maxWidth: ["100%", "100%", "35.625rem", "35.625rem"],
  },
  imageContainer: { alignItems: "center", justifyContent: "center" },
  employeeImage: {
    width: "100px",
    height: "100px",
    borderRadius: "50%",
    position: "absolute",
    top: "-50px",
    left: ["30%", "25%", "20%", "20%"],
    transform: "translateX(-50%)",
    overflow: "hidden",
  },
  employeeImageV2: {
    width: "53px",
    height: "53px",
    borderRadius: "50%",
    overflow: "hidden",
    mr: "1rem",
  },
  feedbackText: {
    ...typography.textMd400,
    display: "inline-block",
    minHeight: "6rem",
  },
  feedbackTextV2: {
    ...typography.textMd400,
    display: "inline-block",
  },
  feedbackTextV3: {
    ...typography.textLg500,
    display: "inline-block",
  },
  divider: {
    mt: "2rem",
    mb: "1.5rem",
    pb: 0,
    borderColor: colors.neutral200,
  },
  dividerV2: {
    my: "1.5rem",
    pb: 0,
    borderColor: colors.neutral200,
  },
  employeeDetails: {
    flexDirection: "column",
  },
  employeeNameStyles: {
    ...typography.textMd500,
    mb: "0.25rem",
  },
  employeeDesignationStyles: {
    ...typography.textMd400,
    color: colors.neutral700,
  },
  empCompanyNameStyles: {
    ...typography.textMd400,
    color: colors.neutral700,
    mt: "0.25rem",
  },
  infoContainer: {
    position: "relative",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    flexWrap: ["wrap-reverse", "wrap-reverse", "nowrap", "nowrap"],
  },
  testimonialBox: {
    backgroundColor: colors.white,
    borderRadius: "20px",
    border: `1px solid ${colors.neutral200}`,
    padding: "32px 16px",
    width: "100%",
  },
  flexColumn: {
    flexDirection: "column",
    height: "100%",
    justifyContent: "space-between",
  },
  companyLogoBox: {
    position: "absolute",
    top: "-0.5rem",
    right: "0rem",
  },
  companyLogoDefaultBox: {
    position: "absolute",
    top: "1rem",
    right: "2rem",
  },
  employeeProfile: {
    alignItems: "center",
    mt: "3rem",
  },
  employeeImageV3: {
    width: ["2.5rem", "2.5rem", "3.25rem", "3.75rem"],
    height: ["2.5rem", "2.5rem", "3.25rem", "3.75rem"],
    borderRadius: "50%",
    overflow: "hidden",
    mr: ["0.75rem", "0.75rem", "1rem", "1rem"],
  },
  bannerContainer: {
    maxWidth: "56.25rem",
    width: ["18.75rem", "18.75rem", "43.75rem", "56.25rem"],
    borderRadius: "0.75rem",
    p: ["1rem", "1rem", "1.5rem", "3rem"],
  },
  bannerFeedback: {
    fontFamily: "Open Sans",
    fontWeight: [
      typography.textMd400.fontWeight,
      typography.textMd400.fontWeight,
      typography.textMd500.fontWeight,
      typography.textLg500.fontWeight,
    ],
    fontSize: [
      typography.textMd400.fontSize,
      typography.textMd400.fontSize,
      typography.textMd500.fontSize,
      typography.textLg500.fontSize,
    ],
    lineHeight: [
      typography.textMd400.lineHeight,
      typography.textMd400.lineHeight,
      typography.textMd500.lineHeight,
      typography.textLg500.lineHeight,
    ],
    p: { m: 0, display: "inline", },
  },
  bannerEmployeeName: {
    fontFamily: [
      typography.textSm.fontFamily,
      typography.textSm.fontFamily,
      typography.textMd400.fontFamily,
      typography.h7.fontFamily,
    ],
    fontWeight: [
      typography.textSm.fontWeight,
      typography.textSm.fontWeight,
      typography.textMd400.fontWeight,
      typography.h7.fontWeight,
    ],
    fontSize: [
      typography.textSm.fontSize,
      typography.textSm.fontSize,
      typography.textMd400.fontSize,
      typography.h7.fontSize,
    ],
    lineHeight: [
      typography.textSm.lineHeight,
      typography.textSm.lineHeight,
      typography.textMd400.lineHeight,
      typography.h7.lineHeight,
    ],
  },
  bannerEmployeeDesignation: {
    color: colors.neutral900,
    fontFamily: "Open Sans",
    fontWeight: [
      typography.textXs.fontWeight,
      typography.textXs.fontWeight,
      typography.textSm.fontWeight,
      typography.textSm.fontWeight,
    ],
    fontSize: [
      typography.textXs.fontSize,
      typography.textXs.fontSize,
      typography.textSm.fontSize,
      typography.textSm.fontSize,
    ],
    lineHeight: [
      typography.textXs.lineHeight,
      typography.textXs.lineHeight,
      typography.textSm.lineHeight,
      typography.textSm.lineHeight,
    ],
  },
  featuredContainerStyles: {
    border: `2px solid ${colors.neutral200}`,
    borderRadius: "1.25rem",
    position: "relative",
    gap: ["1rem", "1.5rem", "1.5rem"],
    padding: "1.875rem",
    alignItems: "flex-start",
    maxWidth: ["26.15rem", "26.15rem", "44rem", "61.75rem"],
    flexDirection: ["column", "column", "row", "row"],
  },
  featuredImageStyles: {
    flexShrink: 0,
    borderRadius: "1rem",
    height: ["16.688rem","19.688rem", "12.75rem", "21.625rem"],
    width: ["100%", "100%", "12.75rem", "21.625rem"],
  },
  featuredIconStyles: {
    position: "absolute",
    top: 0,
    right: "2rem",
    transform: "translateY(-50%)",
    zIndex: 2,
  },
  featuredCaseSectionStyles: {
    gap: "0.5rem",
    mb: "0.75rem",
    cursor: "pointer",
    alignItems: "center",
    flexWrap: "nowrap",
    gap: 2,
  },
  mainContainerStyles: {
    transition: "width 0.3s ease",
    border: `2px solid ${colors.neutral200}`,
    position: "relative",
    gap: 4,
    alignItems: "flex-start",
    borderRadius: "1.25rem",
    mx: "auto",
    maxWidth: "23.75rem",
  },
  mainTextBoxStyles: {
    ...typography.textMd500,
    mb: 2,
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  mainImageBoxStyles: {
    justifyContent: "space-between",
    alignItems: "flex-end",
    px: "1.75rem", 
    py: "1.75rem",
    borderBottomLeftRadius: "1.25rem",
    borderBottomRightRadius: "1.25rem", 
  },
  videoContainerStyles: {
    maxWidth: "23.75rem",
    height: "21.938rem",
    flexDirection: "column",
    borderRadius: "1.25rem",
    overflow: "hidden",
  },
  videoPlayContainerStyles: {
    position: "absolute",
    left: "1.5rem",
    bottom: "1rem",
    borderRadius: "50%",
    width: "3.875rem",
    height: "4.125rem",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  videoInnerStyles: {
    position: "absolute",
    bottom: "1rem",
    right: "1.5rem",
    flexDirection: "column",
    textAlign: "right",
  },
  videoImageStyles: {
    position: "relative",
    width: "100%",
    height: "100%",
    overflow: "hidden",
    cursor: "pointer",
  },
  videoModalStyles: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.8)",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  videoModalCloseStyles: {
    position: "absolute",
    top: "-2.5rem",
    right: 0,
    color: colors.white,
    cursor: "pointer",
    fontSize: "1.5rem",
  },
};

Testimonial.propTypes = {
  variant: PropTypes.oneOf(["default", "primary", "secondary"]).isRequired,
  subVariant: PropTypes.oneOf(["secondaryOne", "secondaryTwo"]).isRequired,
  image: PropTypes.object,
  imageUrl: PropTypes.string,
  reviewUrl: PropTypes.string,
  companyName: PropTypes.string,
  companyLogo: PropTypes.object,
  companyLogoUrl: PropTypes.string,
  companyLogoAltText: PropTypes.string,
  imageAltText: PropTypes.string,
  feedback: PropTypes.string.isRequired,
  employeeName: PropTypes.string,
  employeeDesignation: PropTypes.string,
  labelOne: PropTypes.string,
  labelOneColor: PropTypes.string,
  labelTwo: PropTypes.string,
  labelTwoColor: PropTypes.string,
  caseSection: PropTypes.string,
  caseSectionColor: PropTypes.string,
  caseSectionLink: PropTypes.string,
  rating: PropTypes.number.isRequired,
  backgroundColor: PropTypes.string,
  videoURL: PropTypes.string
};

Testimonial.defaultProps = {
  variant: "default",
  imageUrl: "",
  companyLogoUrl: "",
  companyLogoAltText: "",
  imageAltText: "",
  feedback: "",
  employeeName: "",
  employeeDesignation: "",
  backgroundColor: colors.primary100
};

export default Testimonial;